import type { IOrdinalOrder } from 'api/api'

/** What one of your orders shows in Open orders, and the one action it offers. */
export interface OrdinalOrderRow {
    label: string
    detail: string | null
    action: 'cancel' | 'withdraw' | null
    /** A withdrawal is under way; poll until it settles. */
    pending: boolean
}

// While a withdrawal is under way its row refreshes on its own.
export const PENDING_REFRESH_MS = 15_000

const WITHDRAWAL_ERRORS: Record<string, string> = {
    fee_too_high: 'The funds are too small to withdraw after the network fee.',
    signing_timed_out: 'Your device did not sign in time. Try again with Treechat open.',
    sale_in_progress: 'The owner accepted this offer first.',
    release_incomplete: 'Your remaining funds could not be freed yet. Try again.',
    conflict_unconfirmed: 'Another payment is spending these funds. Try again once it confirms.',
}

const PAUSED_DETAILS: Record<string, string> = {
    broadcast_failed: 'The network did not take the transaction. Try again.',
    unconfirmed: 'Not confirmed yet. Check again later.',
    conflict_unconfirmed: 'Another payment is spending these funds. Try again once it confirms.',
    release_incomplete: 'Your remaining funds could not be freed yet. Try again.',
}

function withdrawalError(error: string | null | undefined): string {
    if (!error) return 'The withdrawal did not finish. Try again.'
    return WITHDRAWAL_ERRORS[error] || 'The withdrawal did not finish. Try again.'
}

/**
 * Open orders lists open asks and bids, plus any signed offer whose funds are still
 * committed (an expired or withdrawn order can still hold them until it is withdrawn
 * on-chain).
 */
export function isListedOrder(order: IOrdinalOrder): boolean {
    if (order.status === 'active' || order.status === 'settling') return true
    if (!order.signed_offer || !order.withdrawal) return false
    if (order.status === 'settled') return false
    return (
        order.withdrawal.withdrawable ||
        order.withdrawal.state === 'broadcast' ||
        order.withdrawal.state === 'confirmed'
    )
}

export function ordinalOrderRow(order: IOrdinalOrder): OrdinalOrderRow {
    if (order.status === 'settling') {
        return { label: 'Selling', detail: null, action: null, pending: false }
    }
    if (order.side === 'ask') {
        if (order.status !== 'active') {
            return { label: order.status, detail: null, action: null, pending: false }
        }
        // A device-signed listing has no in-app way back yet; only an unsigned one cancels.
        return order.cancellable === false
            ? {
                  label: 'Listed',
                  detail: 'Signed on your device, so it can’t be cancelled here yet.',
                  action: null,
                  pending: false,
              }
            : { label: 'Listed', detail: null, action: 'cancel', pending: false }
    }
    if (!order.signed_offer || !order.withdrawal) {
        return order.status === 'active'
            ? { label: 'Offer open', detail: null, action: 'cancel', pending: false }
            : { label: order.status, detail: null, action: null, pending: false }
    }

    const withdrawal = order.withdrawal
    if (withdrawal.stalled) {
        return {
            label: 'Withdrawal paused',
            detail: 'It stopped before finishing. Try again.',
            action: withdrawal.withdrawable ? 'withdraw' : null,
            pending: false,
        }
    }
    // A signed attempt that stopped resumes with the same transaction.
    if (
        withdrawal.paused_reason &&
        (withdrawal.state === 'signed' || withdrawal.state === 'broadcast')
    ) {
        return {
            label: 'Withdrawal paused',
            detail: PAUSED_DETAILS[withdrawal.paused_reason] || 'Try again.',
            action: withdrawal.withdrawable ? 'withdraw' : null,
            pending: false,
        }
    }
    switch (withdrawal.state) {
        case 'requested':
        case 'claimed':
        case 'signed':
            return {
                label: 'Withdrawing',
                detail: 'Signing the transaction that returns your funds.',
                action: null,
                pending: true,
            }
        case 'broadcast':
            return {
                label: 'Withdrawing',
                detail: 'Sent. Your funds are back once it confirms.',
                action: null,
                pending: true,
            }
        case 'confirmed':
        case 'inputs_spent':
            return { label: 'Withdrawn', detail: null, action: null, pending: false }
        case 'failed':
            return {
                label: order.status === 'active' ? 'Offer open' : 'Funds still committed',
                detail: withdrawalError(withdrawal.error),
                action: withdrawal.withdrawable ? 'withdraw' : null,
                pending: false,
            }
        default:
            return {
                label: order.status === 'active' ? 'Offer open' : 'Funds still committed',
                detail:
                    order.status === 'active'
                        ? null
                        : 'Its funds stay committed until you withdraw it.',
                action: withdrawal.withdrawable ? 'withdraw' : null,
                pending: false,
            }
    }
}

export function myOrdinalOrdersPollInterval(orders: IOrdinalOrder[] | undefined): number | false {
    const pending = (orders || []).some(
        order => isListedOrder(order) && ordinalOrderRow(order).pending,
    )
    return pending ? PENDING_REFRESH_MS : false
}
